/**
 * Style & paper: how the report looks and what it prints on.
 *
 * Paper is the first control because it is the one that changes what fits: a report with many
 * columns prints at a different size on Letter than on Legal. The rest — presets, the three
 * switches that matter, a title and a subtitle — fit the panel; the five colours, the Excel
 * palette and the library folder are one click away in a popover over the report.
 */
import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import clsx from 'clsx'
import { Palette, SlidersHorizontal, Wand2 } from 'lucide-react'
import { labelClass, optionClass } from '../studioUi'
import { PAPER_SIZES, REPORT_STYLE_PRESETS } from '../types'
import type { PaperSize, ReportConfig } from '../types'
import { DeckPanel, PopoverPanel, Switch } from './controls'
import { denseField, denseLabel, useDismiss } from './controlStyles'

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) {
  return (
    <label className="block">
      <span className={labelClass}>{label}</span>
      <span className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
          onChange={(event) => onChange(event.target.value)}
          className="h-7 w-9 cursor-pointer rounded border border-stone-700 bg-black/40 p-0.5"
          aria-label={`${label} color picker`}
        />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded border border-stone-700 bg-black/40 px-2 py-1 font-mono text-[11px] text-stone-100 outline-none focus:border-amber-300/40"
          aria-label={`${label} hex value`}
        />
      </span>
    </label>
  )
}

export function StylePanel({
  active,
  config,
  setConfig,
  reportName,
  folders,
}: {
  active: boolean
  config: ReportConfig
  setConfig: Dispatch<SetStateAction<ReportConfig>>
  /** Shown as the title's placeholder: what prints when the title is left empty. */
  reportName: string
  /** Every folder the library already uses, offered when filing this report. */
  folders: string[]
}) {
  const [colorsOpen, setColorsOpen] = useState(false)
  const closeColors = useCallback(() => setColorsOpen(false), [])
  const colorsRef = useDismiss<HTMLDivElement>(colorsOpen, closeColors)

  const setStyle = <K extends keyof ReportConfig['style']>(key: K, value: ReportConfig['style'][K]) =>
    setConfig((current) => ({ ...current, style: { ...current.style, [key]: value } }))
  const setPaper = (paperSize: PaperSize) => setConfig((current) => ({ ...current, paperSize }))
  const paper = PAPER_SIZES.find((option) => option.id === config.paperSize) ?? PAPER_SIZES[1]

  return (
    <DeckPanel
      id="style"
      title="Style & paper"
      icon={<Palette className="h-3.5 w-3.5" />}
      active={active}
      meta={<span>{paper.label} · landscape</span>}
    >
      <div>
        <div className={denseLabel}>Paper size</div>
        <div role="radiogroup" aria-label="Paper size" className="mt-1 grid grid-cols-3 gap-1.5">
          {PAPER_SIZES.map((option) => {
            const selected = option.id === config.paperSize
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setPaper(option.id)}
                className={clsx(
                  'rounded border px-2 py-1 text-left transition',
                  selected
                    ? 'border-amber-300/60 bg-amber-300/[0.10] text-amber-100'
                    : 'border-stone-700 text-stone-300 hover:border-amber-300/30',
                )}
              >
                <span className="block text-[11px] font-medium">{option.label}</span>
                <span className="block text-[9px] text-stone-500">{option.dimensions} · landscape</span>
              </button>
            )
          })}
        </div>
        <p className="mt-1 text-[9px] leading-snug text-stone-600">
          The PDF and the Excel print on this paper, one page wide. PDF and Print stop past the
          readable column count for the paper; Excel stays allowed.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {(Object.keys(REPORT_STYLE_PRESETS) as Array<keyof typeof REPORT_STYLE_PRESETS>).map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => setConfig((current) => ({ ...current, style: { ...REPORT_STYLE_PRESETS[preset] } }))}
            className="inline-flex items-center gap-1 rounded border border-stone-700 px-1.5 py-0.5 text-[10px] text-stone-300 hover:border-amber-300/40 hover:text-amber-200"
          >
            <Wand2 className="h-2.5 w-2.5" /> {preset}
          </button>
        ))}
        <div ref={colorsRef} className="relative ml-auto">
          <button
            type="button"
            onClick={() => setColorsOpen((open) => !open)}
            aria-expanded={colorsOpen}
            className="inline-flex items-center gap-1 rounded border border-stone-700 px-1.5 py-0.5 text-[10px] text-stone-300 hover:border-amber-300/40 hover:text-amber-200"
          >
            <SlidersHorizontal className="h-2.5 w-2.5" /> Colors &amp; export
          </button>
          {colorsOpen ? (
            <PopoverPanel label="Colors and export" align="right" className="w-[22rem]">
              <div className="grid grid-cols-2 gap-2">
                <ColorField label="Header background" value={config.style.headerBg} onChange={(v) => setStyle('headerBg', v)} />
                <ColorField label="Header text" value={config.style.headerText} onChange={(v) => setStyle('headerText', v)} />
                <ColorField label="Row background" value={config.style.rowBg} onChange={(v) => setStyle('rowBg', v)} />
                <ColorField label="Banded row background" value={config.style.rowAltBg} onChange={(v) => setStyle('rowAltBg', v)} />
                <ColorField label="Row text" value={config.style.rowText} onChange={(v) => setStyle('rowText', v)} />
              </div>
              <div className="mt-3 grid gap-2 border-t border-white/[0.06] pt-3">
                <label className="block">
                  <span className={labelClass}>Excel / CSV palette</span>
                  <select
                    value={config.exportTheme}
                    onChange={(event) =>
                      setConfig((current) => ({
                        ...current,
                        exportTheme: event.target.value as ReportConfig['exportTheme'],
                      }))
                    }
                    className={`${denseField} mt-1`}
                  >
                    <option className={optionClass} value="print">
                      Print light (recommended)
                    </option>
                    <option className={optionClass} value="match">
                      Match preview colors
                    </option>
                  </select>
                  <span className="mt-1 block text-[9px] text-stone-600">
                    A workbook is a print-and-share artifact, so exports use a light palette by default. Wrap,
                    banding, and exception flags always follow the preview.
                  </span>
                </label>
                <label className="block">
                  <span className={labelClass}>Library folder</span>
                  <input
                    value={config.folder}
                    onChange={(event) => setConfig((current) => ({ ...current, folder: event.target.value }))}
                    list="studio-folder-options"
                    placeholder="Unfiled — saved with the next version"
                    className={`${denseField} mt-1`}
                  />
                  <datalist id="studio-folder-options">
                    {folders.map((folder) => (
                      <option key={folder} value={folder} />
                    ))}
                  </datalist>
                </label>
              </div>
            </PopoverPanel>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1">
        <Switch
          checked={config.style.zebra}
          onChange={(v) => setStyle('zebra', v)}
          label="Banded rows"
          title="Alternate row background for scanability"
        />
        <Switch
          checked={config.style.wrapText}
          onChange={(v) => setStyle('wrapText', v)}
          label="Word wrap"
          title="Off keeps every column fit to content width"
        />
        <Switch
          checked={config.style.highlightExceptions}
          onChange={(v) => setStyle('highlightExceptions', v)}
          label="Highlight exception rows"
          title="Red flag on rows with open credit / aging exceptions"
        />
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <label className="block min-w-0">
          <span className="sr-only">Report title</span>
          <input
            value={config.title}
            onChange={(event) => setConfig((current) => ({ ...current, title: event.target.value }))}
            placeholder={reportName || 'Report title'}
            className={denseField}
            title="Printed on exports"
          />
        </label>
        <label className="block min-w-0">
          <span className="sr-only">Subtitle</span>
          <input
            value={config.subtitle}
            onChange={(event) => setConfig((current) => ({ ...current, subtitle: event.target.value }))}
            placeholder="Reporting period, audience, notes…"
            className={denseField}
          />
        </label>
      </div>
    </DeckPanel>
  )
}
